import { inject, Injectable } from '@angular/core';
import { ShareableFn, toShareable } from '@co-3/ctrl-ui/common';
import { Contract } from '@models/contract';
import {
    CreateDamageReport,
    DamageReport,
    DamageReportDto,
    DamageReportFileAttachment,
    damageReportFromDto,
    damageReportToDto,
} from '@models/damage-report';
import { InsurenetInterfaceService } from 'app/core/insurenet-interface/insurenet-interface.service';
import { BehaviorSubject, map, Observable, of, switchMap, tap } from 'rxjs';

type DamageReportsPerContractIdMap = Map<number, DamageReport[]>;

@Injectable({
    providedIn: 'root',
})
export class DamageReportsService {
    private insurenetInterfaceService: InsurenetInterfaceService = inject(InsurenetInterfaceService);

    private cachedDamageReportRequests = new Map<number, ShareableFn<DamageReport[]>>();

    private _reports$: BehaviorSubject<DamageReportsPerContractIdMap> =
        new BehaviorSubject<DamageReportsPerContractIdMap>(new Map());

    private selectedContractId$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

    /**
     * @description returns an observable of the whole map of contract ids
     * and associated damage reports. If you need the damage reports for a
     * certain contract it is easier to either select the contract with
     * {@link selectContract} and subscribe to {@link damagesForSelectedContract$},
     * or use {@link get} to fetch the damage reports directly.
     */
    public get reports$(): Observable<DamageReportsPerContractIdMap> {
        return this._reports$.asObservable();
    }

    /**
     * @description returns an observable that contains all damage
     * reports for the currently selected contract. Returns an empty
     * array if there are no reports or no contract selected.
     */
    public get damagesForSelectedContract$(): Observable<DamageReport[]> {
        return this.selectedContractId$.pipe(
            switchMap((id: number | null) => {
                if (typeof id === 'number') {
                    return this.get(id);
                }
                return of([]);
            }),
        );
    }

    /**
     * @description set the currently selected contract. This will update
     * the observable returned  by `damagesForSelectedContract$` with
     * damage reports for the selected contract.
     * @param {Contract|number|null} contract `Contract` or contract id of the
     * contract to be selected, or null to deselect a previously selected
     * contract.
     */
    public selectContract(contract: Contract | number | null): void {
        if (typeof contract === 'object') {
            contract = contract.id;
        }
        this.selectedContractId$.next(contract);
    }

    /**
     * @description get all damage reports for a given contract. Will return
     * already stored reports when available, or try to fetch them when not.
     * @param {Contract|number} contract the contract or contract id to get
     * reports for.
     * @returns {Observable<DamageReport[]>} an observable of all damage reports
     * for the given contract.
     */
    public get(contract: Contract | number): Observable<DamageReport[]>;
    public get(contract: Contract | number, damageId: number): Observable<DamageReport>;
    public get(contract: Contract | number, damageId?: number): Observable<DamageReport[]> | Observable<DamageReport> {
        let reportsPipe$: Observable<DamageReportsPerContractIdMap> = this.reports$;

        if (typeof contract === 'object') {
            contract = contract.id;
        }
        if (this._reports$.getValue().get(contract) == null) {
            reportsPipe$ = this.fetch(contract).pipe(switchMap(() => this.reports$));
        }
        if (typeof damageId === 'number') {
            return reportsPipe$.pipe(
                map((reportsMap: DamageReportsPerContractIdMap) =>
                    reportsMap.get(contract).find((damageReport: DamageReport) => damageReport.id === damageId),
                ),
            );
        }
        return reportsPipe$.pipe(map((reportsMap: DamageReportsPerContractIdMap) => reportsMap.get(contract)));
    }

    /**
     * @description fetch all damage reports for a given contract
     * @param contract the contract or contract id to fetch for
     * @returns {Observable<PaymentMethod[]>} an observable of the damage
     * reports returned by the API
     */
    public fetch(contract: Contract | number): Observable<DamageReport[]> {
        if (typeof contract === 'object') {
            contract = contract.id;
        }
        if (!this.cachedDamageReportRequests.has(contract)) {
            this.cachedDamageReportRequests.set(
                contract,
                toShareable(
                    () =>
                        this.insurenetInterfaceService.getDamages(contract).pipe(
                            map((dtos: DamageReportDto[]) =>
                                dtos.map((dto: DamageReportDto) => damageReportFromDto(dto)),
                            ),
                            tap((reports: DamageReport[]) => {
                                const current = new Map(this._reports$.getValue());
                                current.set(contract, reports);
                                this._reports$.next(current);
                            }),
                        ),
                    { keepFor: '10 min' },
                ),
            );
        }
        return this.cachedDamageReportRequests.get(contract)();
    }

    /**
     * @description create a new damage report and add it to the list of damage reports
     * for the contract.
     * @param {CreateDamageReport} damageReport the damage report to create
     * @returns {Observable<DamageReport>} observable of the created damage report.
     */
    public create(damageReport: CreateDamageReport): Observable<DamageReport> {
        return this.insurenetInterfaceService.reportDamage(damageReportToDto(damageReport)).pipe(
            map((dto: DamageReportDto) => damageReportFromDto(dto)),
            tap((createdReport: DamageReport) => {
                const next = new Map(this._reports$.getValue());
                const reportsForContract = next.get(damageReport.contractId) ?? [];

                reportsForContract.push(createdReport);
                next.set(damageReport.contractId, reportsForContract);

                this._reports$.next(next);
            }),
        );
    }

    public getAttachments(damageId: number): Observable<DamageReportFileAttachment[]> {
        return this.insurenetInterfaceService.getDamageReportFileAttachments(damageId);
    }

    public getDamageDocumentBlob(document: DamageReportFileAttachment): Observable<Blob> {
        return this.insurenetInterfaceService.getDamageDocumentBlob(document.damageId, document.id);
    }

    /**
     * @description removes all stored damage reports and deselects
     * the currently selected contract.
     */
    public clear(): void {
        this._reports$.next(new Map<number, DamageReport[]>());
        this.cachedDamageReportRequests.forEach((cachedRequest) => cachedRequest.drop());
        this.cachedDamageReportRequests.clear();
        this.selectContract(null);
    }
}
